/**
 * writeQueue — persistent, retryable write queue for Supabase operations
 *
 * Guarantees that writes are not silently lost:
 * 1. Each write is persisted to localStorage BEFORE the network request
 * 2. Only removed from storage after the DB confirms success
 * 3. Failed writes are retried with exponential backoff
 * 4. Surviving entries are replayed on next app load or reconnect
 *
 * Supported descriptor shape:
 *   { id, table, op, data, filter, select, upsertOptions, keepOnFail }
 *
 * filter supports: { eq: { col: val }, in: { col: [vals] } }
 * keepOnFail: true — leave in queue after max retries (survives for next session/manual flush)
 */

const QUEUE_KEY = 'prjct-write-queue-v1'
const MAX_RETRIES = 3
// Jittered backoff — stagger retries when multiple writes fail at the same time
// to avoid synchronized thundering herds hammering Supabase simultaneously.
function backoffMs(attempt) {
  const base = [1000, 3000, 8000][attempt] ?? 8000
  return base + Math.random() * (base * 0.3)
}

// ── Queue storage ─────────────────────────────────────────────────────────────

const VALID_OPS = new Set(['insert', 'update', 'upsert', 'delete'])

export function loadQueue() {
  try {
    const raw = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    const clean = raw.filter((e) => e && typeof e === 'object' && typeof e.table === 'string' && VALID_OPS.has(e.op))
    if (clean.length !== raw.length) {
      try { localStorage.setItem(QUEUE_KEY, JSON.stringify(clean)) } catch {}
    }
    return clean
  } catch { return [] }
}

function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)) } catch {}
}

export function enqueue(entry) {
  // Deduplicate: for update ops on the same table+id, replace the older entry
  // so we never re-apply a stale state after recovery
  const q = loadQueue().filter((e) => {
    if (e.op !== 'update' || entry.op !== 'update') return true
    if (e.table !== entry.table) return true
    const eId = e.filter?.eq?.id
    const entryId = entry.filter?.eq?.id
    return !(eId && entryId && eId === entryId)
  })
  q.push({ ...entry, attempts: 0, ts: Date.now() })
  saveQueue(q)
}

function dequeue(id) {
  saveQueue(loadQueue().filter((e) => e.id !== id))
}

// ── Low-level execute ─────────────────────────────────────────────────────────

// 20s timeout: Supabase's gotrue auth lock can take up to 5s to forcefully acquire,
// leaving only ~15s for the actual request — comfortable headroom on a healthy
// connection, enough rope on a slow one. The supabase client's rawFetch sits
// at 25s so this 20s race always fires first, giving callers a clean
// "Write timed out" instead of a raw AbortError.
// With the concurrency limiter (max 2 slots), worst-case stall is 2×20s = 40s, acceptable.
const WRITE_TIMEOUT_MS = 20000

// Concurrency limiter — at most 2 Supabase REST calls in-flight at once.
// Prevents thundering-herd timeouts when many writes queue up simultaneously
// (e.g. completing/moving many tasks while on a slow connection).
const MAX_CONCURRENT_WRITES = 2
let _activeWrites = 0
const _writeQueue = []
function _acquireSlot() {
  return new Promise((resolve) => {
    if (_activeWrites < MAX_CONCURRENT_WRITES) { _activeWrites++; resolve() }
    else _writeQueue.push(resolve)
  })
}
function _releaseSlot() {
  const next = _writeQueue.shift()
  if (next) { next() } else { _activeWrites-- }
}

async function executeDescriptor(descriptor, supabase) {
  await _acquireSlot()
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), WRITE_TIMEOUT_MS)
  // Hard deadline via Promise.race: if abortSignal() doesn't cancel the underlying
  // fetch (known browser/Supabase version quirk), _executeDescriptorInner hangs and
  // _releaseSlot() never runs — permanently holding a concurrency slot and causing
  // every subsequent write to queue forever (infinite saving spinner).
  // clearTimeout(hardTimeoutId) in finally prevents an unhandled rejection if the
  // inner promise resolves before the deadline fires.
  let hardTimeoutId
  const hardDeadline = new Promise((_, reject) => {
    hardTimeoutId = setTimeout(() => reject(new Error('Write timed out')), WRITE_TIMEOUT_MS)
  })
  const started = performance.now()
  try {
    return await Promise.race([
      _executeDescriptorInner(descriptor, supabase, controller.signal),
      hardDeadline,
    ])
  } catch (err) {
    const elapsedMs = Math.round(performance.now() - started)
    if (err.name === 'AbortError' || err.message?.includes('abort') || err.message?.includes('timed out'))
      throw new Error(`Write timed out after ${elapsedMs}ms`)
    throw err
  } finally {
    clearTimeout(timeoutId)
    clearTimeout(hardTimeoutId)
    _releaseSlot()
  }
}

async function _executeDescriptorInner(descriptor, supabase, signal) {
  let query = supabase.from(descriptor.table)

  switch (descriptor.op) {
    case 'insert': query = query.insert(descriptor.data); break
    case 'update': query = query.update(descriptor.data); break
    case 'upsert': query = query.upsert(descriptor.data, descriptor.upsertOptions || {}); break
    case 'delete': query = query.delete(); break
    default: throw new Error(`Unknown op: ${descriptor.op}`)
  }

  if (descriptor.filter?.eq) {
    for (const [col, val] of Object.entries(descriptor.filter.eq)) {
      query = query.eq(col, val)
    }
  }
  if (descriptor.filter?.in) {
    for (const [col, vals] of Object.entries(descriptor.filter.in)) {
      query = query.in(col, vals)
    }
  }

  if (descriptor.select) query = query.select(descriptor.select)
  // Abort signal cancels the actual fetch when the timeout fires,
  // preventing zombie connections from exhausting the browser's connection pool.
  if (signal) query = query.abortSignal(signal)

  const { data, error } = await query

  // 23505 = unique_violation: row already exists — treat as success (idempotent insert)
  if (error?.code === '23505') return { ok: true }
  if (error) return { ok: false, blocked: false, fatal: FATAL_PG_CODES.has(error.code), error }
  if (descriptor.select && (!data || data.length === 0)) return { ok: false, blocked: true }
  return { ok: true }
}

// Lock-free PostgREST executor. Mirrors _executeDescriptorInner's op→REST mapping and result
// contract, but issues a plain fetch() with the caller's CACHED bearer token — it never touches
// the supabase-js client, so it cannot wedge on the in-tab gotrue auth lock. Used only by the
// escalation flush (flushStalePendingWritesRaw) once a wedge has been confirmed by a lock-free
// probe. Returns the SAME shape as _executeDescriptorInner so the drain loop is identical.
async function executeDescriptorRaw(descriptor, { baseUrl, apikey, token, signal }) {
  const params = new URLSearchParams()
  if (descriptor.filter?.eq) {
    for (const [col, val] of Object.entries(descriptor.filter.eq)) params.append(col, `eq.${val}`)
  }
  if (descriptor.filter?.in) {
    for (const [col, vals] of Object.entries(descriptor.filter.in)) {
      params.append(col, `in.(${vals.join(',')})`)
    }
  }

  let method
  let body
  const prefer = []
  switch (descriptor.op) {
    case 'insert': method = 'POST'; body = descriptor.data; break
    case 'upsert':
      method = 'POST'; body = descriptor.data
      prefer.push('resolution=merge-duplicates')
      if (descriptor.upsertOptions?.onConflict) params.set('on_conflict', descriptor.upsertOptions.onConflict)
      break
    case 'update': method = 'PATCH'; body = descriptor.data; break
    case 'delete': method = 'DELETE'; break
    default: throw new Error(`Unknown op: ${descriptor.op}`)
  }
  prefer.push(descriptor.select ? 'return=representation' : 'return=minimal')

  const qs = params.toString()
  const fetchUrl = `${baseUrl}/rest/v1/${descriptor.table}${qs ? `?${qs}` : ''}`
  const headers = {
    apikey,
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Prefer: prefer.join(','),
  }

  let res
  try {
    res = await fetch(fetchUrl, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (err) {
    // Network-level failure (abort/offline) — surface as a non-fatal error so the entry stays queued.
    return { ok: false, blocked: false, fatal: false, error: { message: err?.message || 'fetch failed' } }
  }

  // 23505 = unique_violation: row already exists — idempotent insert success (mirror line 153).
  if (res.status === 409) {
    let code = null
    try { code = (await res.json())?.code } catch {}
    if (code === '23505') return { ok: true }
    return { ok: false, blocked: false, fatal: false, error: { code, message: `HTTP 409 (${code || 'conflict'})` } }
  }

  if (res.ok) {
    if (descriptor.select) {
      let rows = null
      try { rows = await res.json() } catch {}
      if (!rows || rows.length === 0) return { ok: false, blocked: true }
    }
    return { ok: true }
  }

  // Non-2xx, non-409: parse PostgREST error body for a fatal code classification.
  let errBody = null
  try { errBody = await res.json() } catch {}
  const code = errBody?.code
  return {
    ok: false,
    blocked: false,
    fatal: FATAL_PG_CODES.has(code),
    error: { code, message: errBody?.message || `HTTP ${res.status}` },
  }
}

// Postgres error codes that will never succeed on retry — fail fast, don't waste queue slots
const FATAL_PG_CODES = new Set([
  '42703', // undefined_column — column doesn't exist in schema
  '42804', // datatype_mismatch — wrong type for column
  '42P01', // undefined_table
  '42501', // insufficient_privilege — RLS hard denial
  '23502', // not_null_violation
  '23503', // foreign_key_violation
])

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Enqueue a write and execute it with retry.
 * Calls onOk / onBlocked / onFail exactly once — after the FIRST attempt.
 * Remaining retries run silently in the background so the caller's _inFlight
 * counter is released immediately rather than staying elevated for 12–72s.
 *
 * keepOnFail: true — if all retries fail, the entry stays in localStorage so
 * it can be replayed by flushStalePendingWrites on next app load or reconnect.
 * Use this for INSERTs where data loss is unacceptable.
 */
export async function retryWrite(descriptor, supabase, { onOk, onBlocked, onFail, onRetryOk, keepOnFail = false } = {}) {
  enqueue(descriptor)
  let calledBack = false

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, backoffMs(attempt - 1)))
    }

    try {
      const result = await executeDescriptor(descriptor, supabase)

      if (result.blocked) {
        if (!keepOnFail) {
          // No keepOnFail: genuine RLS/permission block — remove immediately
          dequeue(descriptor.id)
          if (!calledBack) { calledBack = true; onBlocked?.() }
          return
        }
        // keepOnFail + blocked: the INSERT for this row may still be in-flight.
        // Retry with backoff — if the INSERT lands, subsequent attempts will succeed.
        if (!calledBack) { calledBack = true; onFail?.(new Error('Row not found — INSERT may still be pending'), { permanent: false }) }
        if (attempt === MAX_RETRIES) {
          // INSERT never landed (or genuine RLS after all). Dequeue — data is lost here
          // but the INSERT entry (if present) will still save the task minus this edit.
          dequeue(descriptor.id)
          return
        }
        console.warn(`[writeQueue] blocked on ${descriptor.table} (attempt ${attempt + 1}/${MAX_RETRIES + 1}) — INSERT in-flight? Retrying…`)
        continue
      }

      if (result.ok) {
        dequeue(descriptor.id)
        if (!calledBack) { calledBack = true; onOk?.() }
        // onRetryOk fires when a background retry succeeds (first attempt already called onFail).
        // Allows callers to clean up state (e.g. _pendingSyncIds) that onOk can't reach.
        else onRetryOk?.()
        return
      }

      // Fatal DB error (schema/type mismatch, hard RLS) — retrying will never help
      if (result.fatal) {
        dequeue(descriptor.id)
        // permanent: true — data was dequeued, will not sync automatically
        if (!calledBack) { calledBack = true; onFail?.(result.error, { permanent: true }) }
        console.error(`[writeQueue] fatal DB error (${result.error?.code}), discarding write:`, result.error?.message)
        return
      }

      // Transient network / DB error — retry with backoff
      // permanent: false on first attempt (data still queued), true only if max retries + not keepOnFail
      if (!calledBack) {
        calledBack = true
        const lastAttempt = attempt === MAX_RETRIES
        const willDiscard = lastAttempt && !keepOnFail
        onFail?.(result.error, { permanent: willDiscard })
      }
      if (attempt === MAX_RETRIES) {
        if (!keepOnFail) dequeue(descriptor.id)
        // keepOnFail: entry stays in queue — will be replayed by flushStalePendingWrites
        return
      }
      console.warn(`[writeQueue] write failed (attempt ${attempt + 1}), retrying in background…`, result.error?.message)
    } catch (err) {
      const isTimeout = err.message?.includes('timed out')
      if (!calledBack) {
        calledBack = true
        // Timeouts mean the connection is dead — entry stays queued for reconnect replay.
        // Non-timeout errors on last attempt are permanent if not keepOnFail.
        const willDiscard = !isTimeout && attempt === MAX_RETRIES && !keepOnFail
        onFail?.(err, { permanent: willDiscard })
      }
      if (isTimeout) {
        // Don't burn retries on a dead connection — queue will replay on reconnect.
        // Entry intentionally NOT dequeued (even without keepOnFail) so it survives.
        console.warn(`[writeQueue] write timed out, handing off to queue for reconnect retry`)
        return
      }
      if (attempt === MAX_RETRIES) {
        if (!keepOnFail) dequeue(descriptor.id)
        return
      }
      console.warn(`[writeQueue] write threw (attempt ${attempt + 1}), retrying in background…`, err?.message)
    }
  }
}

/**
 * Retry a non-persistent write (DELETE — hard to make idempotent).
 * Retries on transient errors; treats Postgres duplicate key (23505) as success.
 * Does NOT persist to localStorage.
 */
export async function retryTransient(fn, { onOk, onBlocked, onFail } = {}) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, backoffMs(attempt - 1)))
    }
    try {
      const { data, error } = await fn()
      // 23505 = unique_violation: row was already inserted on a previous attempt — treat as ok
      if (error?.code === '23505') { onOk?.(); return }
      if (error) {
        if (attempt === MAX_RETRIES) { onFail?.(error); return }
        console.warn(`[writeQueue] transient write failed (attempt ${attempt + 1}), retrying…`, error?.message)
        continue
      }
      if (data !== undefined && (!data || data.length === 0)) { onBlocked?.(); return }
      onOk?.()
      return
    } catch (err) {
      if (attempt === MAX_RETRIES) { onFail?.(err); return }
      console.warn(`[writeQueue] transient write threw (attempt ${attempt + 1}), retrying…`, err?.message)
    }
  }
}

/**
 * Flush writes that survived from a previous session (page was closed mid-write),
 * or that were queued during a connection outage.
 * Called on app init, on reconnect, and by manual "Retry now".
 * Returns true if at least one write was successfully applied (caller should re-fetch).
 *
 * NOTE: Bypasses _acquireSlot intentionally. Background retryWrite loops hold all 3
 * concurrency slots for up to 10s each — routing flush through the same semaphore blocks
 * "Retry now" for 30+ seconds, making it appear broken. This function processes entries
 * sequentially (one at a time), so there is no thundering-herd risk from bypassing the limiter.
 */
// Single-flight guard. setInterval-driven flushers (the active ping poll) don't await
// their callbacks, so without this flag a second tick can launch a fresh flush while
// the previous one is still mid-iteration on the same queue. On flaky connections that
// produces N concurrent attempts for the same entry, all hanging on their own 20s
// timeout — the queue never drains. Returning false tells the caller the flush was
// declined; the in-progress one will catch any newly-enqueued entries by re-reading
// loadQueue() on each iteration.
let _flushInProgress = false
let _flushInProgressSince = 0

// Shared drain loop for both the client-routed and lock-free flushers. `runEntry(entry, signal)`
// returns the executor promise; everything else (single-flight guard, lock-age watchdog, per-entry
// hard-deadline race, dequeue-on-ok/blocked semantics) is identical so the two paths can never
// drift. `via` is a short label for logs.
async function _drainQueue(runEntry, timeoutMs, via) {
  if (_flushInProgress) {
    // Lock-age watchdog. Even with the per-iteration hard-deadline below, a bug elsewhere
    // could leak the lock — and a leaked lock silently disables every future flush, so
    // every queued write would be lost on reload. If the lock has been held longer than
    // 2× the per-entry timeout (sequential drain on a slow link should never need that),
    // assume the holder is dead and take over. Inserts are idempotent (23505 = ok),
    // updates are last-write-wins on the same fields, deletes treat row-not-found as ok.
    const heldMs = Date.now() - _flushInProgressSince
    if (heldMs < timeoutMs * 2) {
      console.info('[writeQueue] flush already in progress, skipping')
      return false
    }
    console.warn(`[writeQueue] flush lock stuck for ${heldMs}ms — forcing release and taking over`)
    _flushInProgress = false
    _flushInProgressSince = 0
  }
  const q = loadQueue()
  if (!q.length) return false

  _flushInProgress = true
  _flushInProgressSince = Date.now()
  console.info(`[writeQueue] flushing ${q.length} pending write(s) via ${via}`)

  let anyFlushed = false
  try {
    for (const entry of q) {
      let hardTid
      try {
        const ctrl = new AbortController()
        const tid = setTimeout(() => ctrl.abort(), timeoutMs)
        let result
        const started = performance.now()
        // Hard deadline via Promise.race. AbortController alone has been observed not
        // to cancel hung Supabase fetches on flaky links — without this race, one
        // unresponsive entry would await forever, locking _flushInProgress permanently
        // and blocking every subsequent flush attempt across the whole session.
        const hardDeadline = new Promise((_, reject) => {
          hardTid = setTimeout(() => reject(new Error('Flush iteration timed out')), timeoutMs)
        })
        try {
          result = await Promise.race([
            runEntry(entry, ctrl.signal),
            hardDeadline,
          ])
        } finally {
          clearTimeout(tid)
          clearTimeout(hardTid)
        }
        if (result.ok || result.blocked) {
          dequeue(entry.id)
          anyFlushed = true
          console.info(`[writeQueue] flushed write for ${entry.table} (${entry.op}) via ${via}`)
        } else if (result.fatal) {
          // Schema/type mismatch, hard RLS — retrying never helps. Drop it so it can't block
          // the queue forever (mirrors retryWrite's fatal handling at line ~223).
          dequeue(entry.id)
          console.error(`[writeQueue] fatal DB error (${result.error?.code}) for ${entry.table} (${entry.op}) — discarding`, result.error?.message)
        } else {
          // Server returned an error but didn't throw — keep the entry queued and log so
          // we can correlate "queue not draining" with the actual reason next time.
          const elapsedMs = Math.round(performance.now() - started)
          console.warn(`[writeQueue] flush attempt for ${entry.table} (${entry.op}) failed in ${elapsedMs}ms — kept in queue`, result.error?.message || result)
        }
      } catch (err) {
        clearTimeout(hardTid)
        const isTimeout = err?.name === 'AbortError' || /abort|timed out/i.test(err?.message || '')
        console.warn(`[writeQueue] flush attempt for ${entry.table} (${entry.op}) ${isTimeout ? 'timed out' : 'threw'} — kept in queue`, err?.message)
      }
    }
  } finally {
    _flushInProgress = false
    _flushInProgressSince = 0
  }
  return anyFlushed
}

export async function flushStalePendingWrites(supabase, { timeoutMs = WRITE_TIMEOUT_MS } = {}) {
  return _drainQueue((entry, signal) => _executeDescriptorInner(entry, supabase, signal), timeoutMs, 'client')
}

/**
 * Escalation flush for a confirmed in-tab auth-lock wedge. Drains the SAME durable queue via
 * direct PostgREST fetch using the caller's cached bearer token — never touches the supabase-js
 * client, so it cannot wedge on the gotrue auth lock that paralyzes flushStalePendingWrites().
 * Shares the single-flight guard with the client path: if a genuine client flush is mid-iteration
 * this is declined (correct — same queue); during a wedge the client flush self-releases via its
 * per-entry hard-deadline, after which this takes over.
 */
export async function flushStalePendingWritesRaw({ baseUrl, apikey, token, timeoutMs = WRITE_TIMEOUT_MS }) {
  if (!baseUrl || !apikey || !token) return false
  return _drainQueue((entry, signal) => executeDescriptorRaw(entry, { baseUrl, apikey, token, signal }), timeoutMs, 'raw')
}

/** Returns the number of writes currently in the queue. */
export function pendingWriteCount() {
  return loadQueue().length
}

// ── Pending registry (reconciliation source of truth) ──────────────────────────
//
// An entity is "pending" as long as ANY unconfirmed write targeting it sits in
// the durable queue — across timeouts, background retries, and page reloads.
// This is strictly stronger than an in-memory "in-flight" window: it stays true
// until the write actually succeeds and is dequeued. Reconciliation (realtime
// echoes, refetches, the loadUserData overlay) consults this so a stale server
// row never clobbers a local edit that hasn't landed yet.

function entityIdsOf(entry, add) {
  if (entry.op === 'delete') {
    if (entry.filter?.eq?.id != null) add(entry.filter.eq.id)
    if (Array.isArray(entry.filter?.in?.id)) {
      for (const id of entry.filter.in.id) if (id != null) add(id)
    }
    return
  }
  // insert / update / upsert — data may be a single row or (defensively) an array
  const datas = Array.isArray(entry.data) ? entry.data : entry.data ? [entry.data] : []
  let found = false
  for (const d of datas) {
    if (d?.id != null) { add(d.id); found = true }
  }
  // updates carry the target in filter.eq.id even when data has no id
  if (!found && entry.filter?.eq?.id != null) add(entry.filter.eq.id)
}

/**
 * Map<table, Set<id>> of every entity that currently has a queued write.
 * Derived fresh from localStorage on each call — no separate bookkeeping to drift.
 */
export function pendingEntityIds() {
  const m = new Map()
  for (const e of loadQueue()) {
    entityIdsOf(e, (id) => {
      if (!m.has(e.table)) m.set(e.table, new Set())
      m.get(e.table).add(id)
    })
  }
  return m
}

/** True iff the durable queue holds any unconfirmed write targeting (table, id). */
export function isEntityPending(table, id) {
  for (const e of loadQueue()) {
    if (e.table !== table) continue
    let hit = false
    entityIdsOf(e, (eid) => { if (eid === id) hit = true })
    if (hit) return true
  }
  return false
}

/**
 * Returns the number of writes that have been in the queue for longer than minAgeMs.
 * Use this for the sync health indicator to avoid false positives from writes that
 * are in-flight (enqueued immediately on edit, but dequeued within ~1-2s on success).
 * Only entries that are genuinely stuck (past the normal write completion window) should
 * be surfaced to the user.
 */
export function stalePendingWriteCount(minAgeMs = 15000) {
  const now = Date.now()
  return loadQueue().filter((e) => now - e.ts > minAgeMs).length
}

/**
 * Remove pending INSERT entries for the given row IDs.
 * Call this when locally-created rows are intentionally deleted before their INSERT confirms,
 * so the INSERT doesn't race back and re-create a task the user just deleted.
 */
export function cancelQueuedInserts(table, ids) {
  const idSet = new Set(ids)
  const q = loadQueue().filter((e) => !(e.table === table && e.op === 'insert' && idSet.has(e.data?.id)))
  saveQueue(q)
}

/**
 * Manually wipe the entire queue. Escape hatch for users whose queue is stuck on
 * unrecoverable entries (e.g. writes from before a schema change). Returns the
 * number of entries cleared. The caller should reload data afterward so the UI
 * reflects whatever actually landed server-side.
 */
export function clearAllPendingWrites() {
  const q = loadQueue()
  const n = q.length
  saveQueue([])
  return n
}
